import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Copy, Check, User, Calendar, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { BlueprintDisplay } from './BlueprintDisplay';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { trackSharedBlueprintViewed } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SharedBlueprintView = ({ shortId, onBack }) => {
    const [blueprint, setBlueprint] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchBlueprint = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${API}/shared/${shortId}`);
                setBlueprint(response.data);
                
                // Track the shared blueprint view
                trackSharedBlueprintViewed(shortId, response.data?.blueprint?.app_name);
            } catch (err) {
                setError(err.response?.data?.detail || 'Blueprint not found');
            } finally {
                setLoading(false);
            }
        };

        if (shortId) {
            fetchBlueprint();
        }
    }, [shortId]);

    const copyShareLink = () => {
        const url = `${window.location.origin}/b/${shortId}`;
        navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success('Link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <motion.div
                    className="flex flex-col items-center gap-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <div className="w-12 h-12 border-4 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
                    <p className="text-zinc-400">Loading shared blueprint...</p>
                </motion.div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="text-6xl mb-4">🔍</div>
                    <h2 className="text-xl font-semibold text-white mb-2">Blueprint Not Found</h2>
                    <p className="text-zinc-400 mb-6">{error}</p>
                    <Button onClick={onBack} variant="outline" data-testid="back-to-home-btn">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Home
                    </Button>
                </motion.div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
        >
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <Button 
                    onClick={onBack} 
                    variant="ghost" 
                    className="text-zinc-400 hover:text-white"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                </Button>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <User className="w-4 h-4" />
                        <span>Shared by {blueprint.creator_email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(blueprint.created_at)}</span>
                    </div>
                    <Button
                        onClick={copyShareLink}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        data-testid="copy-share-link-btn"
                    >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied ? 'Copied!' : 'Copy Link'}
                    </Button>
                </div>
            </div>

            {/* Shared Badge */}
            <div className="bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 rounded-lg p-4">
                <div className="flex items-center gap-3">
                    <ExternalLink className="w-5 h-5 text-violet-400" />
                    <div>
                        <p className="text-sm font-medium text-white">Shared Blueprint</p>
                        <p className="text-xs text-zinc-400">Original idea: "{blueprint.idea}"</p>
                    </div>
                </div>
            </div>

            {/* Blueprint Display */}
            <BlueprintDisplay 
                blueprint={blueprint.blueprint} 
                idea={blueprint.idea}
                isSharedView={true}
            />
        </motion.div>
    );
};
